import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  BOARD_CIRCUIT_FILTERS,
  BOARD_SORTS,
  DEFAULT_BOARD_FILTERS,
  type BoardCircuitFilter,
  type BoardFilters,
  type BoardSortOption,
  type GradeOption,
} from './board-filters';

const ANY = 'any';

interface BoardFiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: BoardFilters;
  onApply: (filters: BoardFilters) => void;
  /** The board's scale, easiest first — the two ends of the range slider. */
  grades: GradeOption[];
  /** Angles this board holds problems at; empty hides the angle filter. */
  inclinations: number[];
  /** "Sent" is personal, so there is nothing to ask a logged-out visitor. */
  loggedIn: boolean;
}

/**
 * The filters behind the toolbar's "Filters" button.
 *
 * Edits a draft and commits it on Apply rather than filtering as you drag: each
 * change is a request against a library of hundreds of problems, and a range
 * slider fires a great many of them on the way to where you meant to stop.
 */
export default function BoardFiltersDialog({
  open,
  onOpenChange,
  filters,
  onApply,
  grades,
  inclinations,
  loggedIn,
}: BoardFiltersDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<BoardFilters>(filters);

  // Reopening starts from what is actually applied, so an abandoned edit does
  // not linger.
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  // The slider works in positions on the scale; the ends of an open range are
  // the ends of the scale.
  const lastIndex = Math.max(grades.length - 1, 0);
  const minIndex = draft.gradeMin ? Math.max(grades.findIndex((g) => g.id === draft.gradeMin), 0) : 0;
  const maxIndex = draft.gradeMax
    ? (() => {
        const found = grades.findIndex((g) => g.id === draft.gradeMax);
        return found === -1 ? lastIndex : found;
      })()
    : lastIndex;

  const setRange = ([from, to]: number[]) =>
    setDraft((prev) => ({
      ...prev,
      // A range covering the whole scale is no filter at all, and storing it as
      // one would light the badge up for nothing.
      gradeMin: from === 0 ? null : grades[from].id,
      gradeMax: to === lastIndex ? null : grades[to].id,
    }));

  const apply = (next: BoardFilters) => {
    onApply(next);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('climbs.board.filtersTitle')}</DialogTitle>
          <DialogDescription>{t('climbs.board.filtersDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {grades.length > 1 && (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <Label>{t('climbs.board.filterGrade')}</Label>
                <span className="text-sm text-muted-foreground">
                  {grades[minIndex]?.name} – {grades[maxIndex]?.name}
                </span>
              </div>
              <Slider
                value={[minIndex, maxIndex]}
                min={0}
                max={lastIndex}
                step={1}
                minStepsBetweenThumbs={0}
                onValueChange={setRange}
                aria-label={t('climbs.board.filterGrade')}
              />
            </div>
          )}

          {loggedIn && (
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="board-unsent-only" className="font-normal">
                {t('climbs.board.unsentOnly')}
              </Label>
              <Switch
                id="board-unsent-only"
                checked={draft.unsentOnly}
                onCheckedChange={(checked) => setDraft((prev) => ({ ...prev, unsentOnly: checked }))}
              />
            </div>
          )}

          {inclinations.length > 0 && (
            <div className="space-y-2">
              <Label>{t('climbs.board.filterInclination')}</Label>
              <Select
                value={draft.inclination === null ? ANY : String(draft.inclination)}
                onValueChange={(value) =>
                  setDraft((prev) => ({ ...prev, inclination: value === ANY ? null : Number(value) }))
                }
              >
                <SelectTrigger aria-label={t('climbs.board.filterInclination')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>{t('climbs.board.allInclinations')}</SelectItem>
                  {inclinations.map((angle) => (
                    <SelectItem key={angle} value={String(angle)}>
                      {angle}°
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Offered on every board: any problem can be set as a circuit, with
              or without lights to play it on. */}
          <div className="space-y-2">
            <Label>{t('climbs.board.filterCircuit')}</Label>
            <Select
              value={draft.circuit}
              onValueChange={(value) =>
                setDraft((prev) => ({ ...prev, circuit: value as BoardCircuitFilter }))
              }
            >
              <SelectTrigger aria-label={t('climbs.board.filterCircuit')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BOARD_CIRCUIT_FILTERS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`climbs.board.circuits.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('climbs.board.sortBy')}</Label>
            <Select
              value={draft.sort}
              onValueChange={(value) => setDraft((prev) => ({ ...prev, sort: value as BoardSortOption }))}
            >
              <SelectTrigger aria-label={t('climbs.board.sortBy')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BOARD_SORTS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`climbs.board.sorts.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => apply(DEFAULT_BOARD_FILTERS)}>
            {t('climbs.board.resetFilters')}
          </Button>
          <Button type="button" onClick={() => apply(draft)}>
            {t('climbs.board.applyFilters')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
